import EmblaCarousel from "embla-carousel";

/**
 * ProductRecommendations Web Component
 *
 * Fetcha i prodotti raccomandati tramite la Shopify Product Recommendations API
 * abbinata alla Section Rendering API (section_id=product-recommendations).
 * Popola il carousel Embla con le card renderizzate lato server.
 *
 * Endpoint:
 *   GET /recommendations/products
 *     ?product_id={id}
 *     &intent={related|complementary}
 *     &limit={n}
 *     &section_id=product-recommendations
 */
class ProductRecommendations extends HTMLElement {
  async connectedCallback() {
    this._productId = this.dataset.productId;
    this._intent = this.dataset.intent || "related";
    this._limit = this.dataset.limit || "10";
    this._sectionId = this.dataset.sectionId;
    this._baseUrl = this.dataset.url;
    this._emblaApi = null;
    this._emblaUpdateButtons = null;
    this._onPrevClick = null;
    this._onNextClick = null;
    this._prevBtn = null;
    this._nextBtn = null;

    if (!this._productId) return;

    await this._fetchAndRender();
  }

  disconnectedCallback() {
    this._destroyEmbla();
  }

  /**
   * Chiama la Recommendations API + Section Rendering API in un'unica fetch.
   * Usa data-url come base (routes.product_recommendations_url da Liquid).
   */
  async _fetchAndRender() {
    const base = this._baseUrl || "/recommendations/products";
    const url = new URL(base, window.location.origin);
    url.searchParams.set("product_id", this._productId);
    url.searchParams.set("intent", this._intent);
    url.searchParams.set("limit", this._limit);
    url.searchParams.set("section_id", "product-recommendations");

    try {
      const res = await fetch(url.toString(), {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!res.ok) {
        console.warn("[ProductRecommendations] Fetch fallita:", res.status);
        this._showEmpty();
        return;
      }

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // Estrae gli slide .embla__slide dall'HTML della sezione renderizzata
      const slides = doc.querySelectorAll(".embla__slide");

      if (!slides.length) {
        this._showEmpty();
        return;
      }

      // Sostituisce gli skeleton con i prodotti veri
      const container = this.querySelector(".embla__container");
      this._destroyEmbla();
      container.innerHTML = "";
      slides.forEach((slide) => container.appendChild(slide));

      this._initEmbla();
    } catch (err) {
      console.error("[ProductRecommendations] Errore:", err);
      this._showEmpty();
    }
  }

  /**
   * Inizializza Embla sul viewport e collega i bottoni prev/next.
   * I bottoni si trovano nell'header della sezione, fuori dal Web Component.
   */
  _initEmbla() {
    const viewportNode = this.querySelector(".embla__viewport");
    if (!viewportNode) return;

    // I bottoni sono nel <header> fratello del componente, dentro la stessa sezione
    const section = this.closest(".shopify-section") || this.parentElement;
    const prevBtn = section?.querySelector(".product-recommendations__prev");
    const nextBtn = section?.querySelector(".product-recommendations__next");
    this._prevBtn = prevBtn;
    this._nextBtn = nextBtn;

    const isMobile = window.matchMedia("(max-width: 1024px)").matches;

    const emblaOptions = {
      loop: false,
      align: "start"
    };

    if (isMobile) {
      emblaOptions.containScroll = "trimSnaps";
      emblaOptions.slidesToScroll = 1;
    }

    const emblaApi = EmblaCarousel(viewportNode, emblaOptions);
    this._emblaApi = emblaApi;

    const updateButtons = () => {
      const hasSlides = emblaApi.slideNodes().length > 0;
      const disableAll = !hasSlides;

      if (prevBtn) prevBtn.disabled = disableAll || !emblaApi.canScrollPrev();
      if (nextBtn) nextBtn.disabled = disableAll || !emblaApi.canScrollNext();
    };
    this._emblaUpdateButtons = updateButtons;

    this._onPrevClick = () => emblaApi.scrollPrev();
    this._onNextClick = () => emblaApi.scrollNext();

    if (prevBtn) prevBtn.addEventListener("click", this._onPrevClick);
    if (nextBtn) nextBtn.addEventListener("click", this._onNextClick);

    emblaApi.on("select", updateButtons);
    emblaApi.on("reInit", updateButtons);
    updateButtons();
  }

  _destroyEmbla() {
    if (this._prevBtn && this._onPrevClick) {
      this._prevBtn.removeEventListener("click", this._onPrevClick);
    }
    if (this._nextBtn && this._onNextClick) {
      this._nextBtn.removeEventListener("click", this._onNextClick);
    }

    this._onPrevClick = null;
    this._onNextClick = null;
    this._prevBtn = null;
    this._nextBtn = null;

    if (this._emblaApi) {
      if (this._emblaUpdateButtons) {
        this._emblaApi.off("select", this._emblaUpdateButtons);
        this._emblaApi.off("reInit", this._emblaUpdateButtons);
      }
      this._emblaApi.destroy();
      this._emblaApi = null;
    }

    this._emblaUpdateButtons = null;
  }

  /**
   * Nasconde l'intero componente (inclusi header, titolo e bottoni)
   * quando non ci sono raccomandazioni disponibili.
   */
  _showEmpty() {
    this.hidden = true;
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
